import React, { useEffect, useMemo, useState } from "react";
import { fetchPharmacistRecommendations } from "../services/pharmacistRecommendationService";

const emptyItem = {
  medicineName: "",
  recommendation: "",
  quantity: 1,
  price: "",
};

const getQuotationId = (order) => order?.quotationId || order?.id || "-";

const getRecommendationMedicineLabel = (recommendation) =>
  recommendation.medicineName ||
  recommendation.medicine?.name ||
  recommendation.name ||
  "";

const getMatchingRecommendations = (recommendations, searchText) => {
  const normalizedSearchText = searchText.trim().toLowerCase();

  if (!normalizedSearchText) return recommendations.slice(0, 8);

  return recommendations
    .filter((recommendation) =>
      getRecommendationMedicineLabel(recommendation)
        .toLowerCase()
        .includes(normalizedSearchText),
    )
    .slice(0, 8);
};

const findMatchingRecommendation = (recommendations, medicineName) => {
  const normalizedMedicineName = medicineName.trim().toLowerCase();

  if (!normalizedMedicineName) return null;

  return recommendations.find(
    (recommendation) =>
      getRecommendationMedicineLabel(recommendation).trim().toLowerCase() ===
      normalizedMedicineName,
  );
};

const isCompleteQuoteItem = (item) =>
  item.medicineName.trim() &&
  Number(item.quantity) > 0 &&
  Number(item.price) > 0;

const QuoteModal = ({ show, order, saving, error, onClose, onSubmit }) => {
  const [responseNotes, setResponseNotes] = useState("");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [formError, setFormError] = useState("");
  const [openMedicineIndex, setOpenMedicineIndex] = useState(null);

  useEffect(() => {
    if (!show) return;

    setResponseNotes("");
    setItems([{ ...emptyItem }]);
    setFormError("");
    setOpenMedicineIndex(null);

    const loadRecommendations = async () => {
      try {
        setLoadingRecommendations(true);
        const recommendationList = await fetchPharmacistRecommendations();
        setRecommendations(
          recommendationList.filter(
            (recommendation) =>
              recommendation.isActive !== false &&
              getRecommendationMedicineLabel(recommendation).trim(),
          ),
        );
      } catch (loadError) {
        setRecommendations([]);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    loadRecommendations();
  }, [show]);

  const quoteTotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + (Number(item.price) || 0) * (Number(item.quantity) || 1),
        0,
      ),
    [items],
  );

  if (!show) return null;

  const handleItemChange = (index, field, value) => {
    const matchedRecommendation =
      field === "medicineName"
        ? findMatchingRecommendation(recommendations, value)
        : null;

    setItems((previousItems) =>
      previousItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: value,
              ...(field === "medicineName"
                ? {
                    recommendation: matchedRecommendation?.recommendation || "",
                  }
                : {}),
            }
          : item,
      ),
    );
  };

  const handleMedicineSelect = (index, recommendation) => {
    setItems((previousItems) =>
      previousItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              medicineName: getRecommendationMedicineLabel(recommendation),
              recommendation: recommendation.recommendation || "",
            }
          : item,
      ),
    );
    setOpenMedicineIndex(null);
  };

  const addItem = () => {
    if (!items.every(isCompleteQuoteItem)) {
      setFormError("Fill medicine, quantity, and price before adding another medicine.");
      return;
    }

    setFormError("");
    setItems((previousItems) => [...previousItems, { ...emptyItem }]);
  };

  const removeItem = (index) => {
    setItems((previousItems) =>
      previousItems.length === 1
        ? previousItems
        : previousItems.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const validItems = items.filter(isCompleteQuoteItem);

    if (validItems.length !== items.length) {
      setFormError("Fill medicine, quantity, and price for each medicine.");
      return;
    }

    setFormError("");

    const payload = {
      responseNotes: responseNotes.trim(),
      items: validItems.map((item) => {
        const quantity = Number(item.quantity) || 1;
        const price = Number(item.price) || 0;

        return {
          medicineName: item.medicineName.trim(),
          price,
          quantity,
        };
      }),
    };

    onSubmit(payload);
  };

  return (
    <>
      <div
        className="modal show healthid_request"
        style={{ display: "block" }}
        tabIndex="-1"
        onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      >
        <div className="modal-dialog modal-dialog-centered quote-modal-dialog">
          <div className="modal-content quote-modal-content">
            <div className="modal-header border-none d-flex align-items-start justify-content-between">
              <div className="d-flex flex-column">
                <h1 className="modal-title fs-5">Quote Generation</h1>
                <p className="patient_name">
                  Quotation #{getQuotationId(order)} · Pharmacist response
                </p>
              </div>

              <button type="button" className="close-btns" onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="m15 9-6 6" />
                  <path d="m9 9 6 6" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="quote-modal-grid">
                  <section className="quote-builder-panel">
                    <div className="mb-3">
                      <label className="form-label" htmlFor="responseNotes">
                        Pharmacist Notes
                      </label>
                      <textarea
                        id="responseNotes"
                        className="form-control bg-img-none"
                        rows="2"
                        value={responseNotes}
                        onChange={(event) => setResponseNotes(event.target.value)}
                      />
                    </div>

                    <div className="quote-line-header">
                      <span>Medicine</span>
                      <span>Qty</span>
                      <span>Manual Price</span>
                      <span>Line Total</span>
                      <span></span>
                    </div>

                    {items.map((item, index) => {
                      const lineTotal =
                        (Number(item.price) || 0) * (Number(item.quantity) || 1);
                      const matchingRecommendations = getMatchingRecommendations(
                        recommendations,
                        item.medicineName,
                      );
                      const showMedicineOptions = openMedicineIndex === index;

                      return (
                        <div className="quote-line-item" key={index}>
                          <div className="quote-medicine-cell">
                            <input
                              className="form-control bg-img-none"
                              placeholder={
                                loadingRecommendations
                                  ? "Loading recommendations..."
                                  : "Search recommended medicine"
                              }
                              value={item.medicineName}
                              onChange={(event) =>
                                handleItemChange(index, "medicineName", event.target.value)
                              }
                              onFocus={() => setOpenMedicineIndex(index)}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setOpenMedicineIndex((currentIndex) =>
                                    currentIndex === index ? null : currentIndex,
                                  );
                                }, 120);
                              }}
                              disabled={loadingRecommendations}
                            />
                            {showMedicineOptions && (
                              <div className="quote-medicine-options">
                                {matchingRecommendations.length > 0 ? (
                                  matchingRecommendations.map((recommendation) => {
                                    const medicineName =
                                      getRecommendationMedicineLabel(recommendation);

                                    return (
                                      <button
                                        type="button"
                                        key={recommendation.id}
                                        className="quote-medicine-option"
	                                        onMouseDown={(event) => event.preventDefault()}
	                                        onClick={() =>
	                                          handleMedicineSelect(index, recommendation)
	                                        }
	                                      >
                                        {medicineName}
                                      </button>
                                    );
                                  })
                                ) : (
                                  <div className="quote-medicine-empty">
                                    No recommendations found
	                              </div>
	                            )}
	                            {item.recommendation && (
	                              <p className="quote-recommendation-copy">
	                                {item.recommendation}
	                              </p>
	                            )}
	                          </div>
                            )}
                          </div>

                          <input
                            type="number"
                            min="1"
                            className="form-control bg-img-none"
                            value={item.quantity}
                            onChange={(event) =>
                              handleItemChange(index, "quantity", event.target.value)
                            }
                          />

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="form-control bg-img-none"
                            value={item.price}
                            onChange={(event) =>
                              handleItemChange(index, "price", event.target.value)
                            }
                          />

                          <strong className="quote-line-total">
                            {lineTotal.toFixed(2)} NIS
                          </strong>

                          <button
                            type="button"
                            className="remove_row"
                            onClick={() => removeItem(index)}
                            aria-label="Remove medicine line"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="none"
                              stroke="#FB2C36"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>

                        </div>
                      );
                    })}

                    <div className="quote-actions-row">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addItem}
                      >
                        + Add Medicine
                      </button>
                    </div>
                  </section>
                </div>

                {(formError || error) && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {formError || error}
                  </div>
                )}

                <div className="total-price-bottom mt-4">
                  <div className="total-price d-flex align-items-center justify-content-between">
                    <span className="price-text d-flex align-items-center text-white fw-semibold">
                      Quote Total: {quoteTotal.toFixed(2)} NIS
                    </span>

                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Sending..." : "Send Quote to Customer"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
    </>
  );
};

export default QuoteModal;
